import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Camera, Scale, Trash2, Upload, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Display config — controls which fields appear in the editable form,
// the latest-snapshot KPI grid and the trend-chart picker.
const FIELDS = [
    { key: 'weight_kg',                 unit: 'kg',     label_de: 'Gewicht',          label_en: 'Weight',          decimals: 1 },
    { key: 'body_fat_pct',              unit: '%',      label_de: 'Körperfett',       label_en: 'Body fat',        decimals: 1 },
    { key: 'fat_mass_kg',               unit: 'kg',     label_de: 'Fettmasse',        label_en: 'Fat mass',        decimals: 1 },
    { key: 'muscle_mass_kg',            unit: 'kg',     label_de: 'Muskelmasse',      label_en: 'Muscle mass',     decimals: 1 },
    { key: 'skeletal_muscle_kg',        unit: 'kg',     label_de: 'Skelettmuskel',    label_en: 'Skeletal muscle', decimals: 1 },
    { key: 'bone_mass_kg',              unit: 'kg',     label_de: 'Knochenmasse',     label_en: 'Bone mass',       decimals: 1 },
    { key: 'protein_kg',                unit: 'kg',     label_de: 'Eiweiß',           label_en: 'Protein',         decimals: 1 },
    { key: 'water_pct',                 unit: '%',      label_de: 'Wasser',           label_en: 'Water',           decimals: 1 },
    { key: 'bmi',                       unit: '',       label_de: 'BMI',              label_en: 'BMI',             decimals: 1 },
    { key: 'visceral_fat',              unit: '',       label_de: 'Viszeralfett',     label_en: 'Visceral fat',    decimals: 0 },
    { key: 'basal_metabolic_rate_kcal', unit: 'kcal',   label_de: 'Grundumsatz',      label_en: 'BMR',             decimals: 0 },
    { key: 'fat_free_mass_kg',          unit: 'kg',     label_de: 'Fettfreie Masse',  label_en: 'Fat-free mass',   decimals: 1 },
    { key: 'subcutaneous_fat_pct',      unit: '%',      label_de: 'Subkutanes Fett',  label_en: 'Subcut. fat',     decimals: 1 },
    { key: 'smi',                       unit: 'kg/m²', label_de: 'SMI',              label_en: 'SMI',             decimals: 1 },
    { key: 'body_age',                  unit: 'J',      label_de: 'Körperalter',      label_en: 'Body age',        decimals: 0 },
    { key: 'whr',                       unit: '',       label_de: 'WHR',              label_en: 'WHR',             decimals: 2 },
    { key: 'body_score',                unit: '/100',   label_de: 'Körperpunktzahl',  label_en: 'Body score',      decimals: 0 },
];

const KPI_FIELDS = ['weight_kg', 'body_fat_pct', 'skeletal_muscle_kg', 'visceral_fat', 'bmi', 'smi'];

const fmt = (v, decimals) => {
    if (v === null || v === undefined || isNaN(v)) return '—';
    return Number(v).toFixed(decimals);
};

const labelFor = (key, language) => {
    const f = FIELDS.find(x => x.key === key);
    return f ? (language === 'de' ? f.label_de : f.label_en) : key;
};

export default function BodyCompositionTracker() {
    const { language } = useLanguage();
    const isDe = language === 'de';
    const fileInputRef = useRef(null);

    const [latest, setLatest] = useState(null);
    const [series, setSeries] = useState({});
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [editing, setEditing] = useState(null); // pre-fill object for the form
    const [chartField, setChartField] = useState('weight_kg');
    const [showHistory, setShowHistory] = useState(false);


    const loadAll = async () => {
        try {
            setLoading(true);
            const [trends, list] = await Promise.all([
                axios.get(`${API}/api/body-composition/trends?days=365`),
                axios.get(`${API}/api/body-composition?limit=50`),
            ]);
            setLatest(trends.data.latest);
            setSeries(trends.data.series || {});
            setHistory(list.data || []);
        } catch (err) {
            console.error('body-comp load failed:', err);
            toast.error(isDe ? 'Konnte Körperdaten nicht laden' : 'Could not load body data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
    }, []);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error(isDe ? 'Datei zu gross (max 10MB)' : 'File too large (max 10MB)');
            return;
        }
        try {
            setExtracting(true);
            // Read as data URL → strip prefix → send to backend
            const dataUrl = await new Promise((res, rej) => {
                const r = new FileReader();
                r.onload = () => res(r.result);
                r.onerror = rej;
                r.readAsDataURL(file);
            });
            const b64 = dataUrl.split(',')[1];
            const res = await axios.post(
                `${API}/api/body-composition/extract`,
                { image_base64: b64, image_type: file.type || 'image/jpeg' },
                { timeout: 90000 },
            );
            // Open editable form pre-filled with extracted values
            setEditing(res.data);
            toast.success(isDe ? 'Werte erkannt — bitte prüfen' : 'Values extracted — please review');
        } catch (err) {
            console.error('extract failed:', err);
            const detail = err?.response?.data?.detail || err?.message;
            toast.error((isDe ? 'KI-Extraktion fehlgeschlagen: ' : 'AI extraction failed: ') + String(detail).slice(0, 120));
        } finally {
            setExtracting(false);
        }
    };

    const startManual = () => setEditing({});
    const cancelEdit = () => setEditing(null);

    const updateField = (key, raw) => {
        setEditing(prev => {
            const next = { ...prev };
            if (raw === '' || raw === null) {
                delete next[key];
            } else {
                const num = Number(raw);
                if (!isNaN(num)) next[key] = num;
            }
            return next;
        });
    };

    const saveEntry = async () => {
        // Drop measured_at_iso, source defaults already on backend.
        const payload = { ...editing };
        if (payload.measured_at_iso) {
            payload.measured_at = payload.measured_at_iso;
            delete payload.measured_at_iso;
        }
        // Keep only known fields + a few meta.
        try {
            await axios.post(`${API}/api/body-composition`, payload);
            toast.success(isDe ? 'Messung gespeichert' : 'Measurement saved');
            setEditing(null);
            loadAll();
        } catch (err) {
            console.error('save failed:', err);
            toast.error(isDe ? 'Speichern fehlgeschlagen' : 'Save failed');
        }
    };

    const deleteEntry = async (id) => {
        if (!window.confirm(isDe ? 'Diese Messung löschen?' : 'Delete this measurement?')) return;
        try {
            await axios.delete(`${API}/api/body-composition/${id}`);
            loadAll();
        } catch (err) {
            console.error('delete failed:', err);
            toast.error(isDe ? 'Löschen fehlgeschlagen' : 'Delete failed');
        }
    };

    // Chart data → recharts needs simple {x,y} arrays
    const chartData = (series[chartField] || []).map(p => ({
        date: new Date(p.date).toLocaleDateString(isDe ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit' }),
        value: p.value,
    }));

    const fieldsWithData = FIELDS.filter(f => (series[f.key] || []).length > 0);

    return (
        <div className="space-y-3" data-testid="body-composition-tracker">
            {/* Header + actions */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Scale className="w-4 h-4 text-primary" />
                    <h3 className="text-base font-semibold">{isDe ? 'Körperzusammensetzung' : 'Body composition'}</h3>
                </div>
                <div className="flex items-center gap-1.5">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        className="hidden"
                        onChange={handleFile}
                        data-testid="bc-upload-input"
                    />
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={extracting}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="bc-upload-btn"
                        className="gap-1.5"
                    >
                        {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                        <span className="text-xs">{isDe ? 'Foto/PDF' : 'Photo/PDF'}</span>
                    </Button>
                    <Button
                        size="sm"
                        onClick={startManual}
                        data-testid="bc-add-manual-btn"
                        className="gap-1.5"
                    >
                        <Upload className="w-3.5 h-3.5" />
                        <span className="text-xs">{isDe ? 'Manuell' : 'Manual'}</span>
                    </Button>
                </div>
            </div>

            {/* Latest KPI grid */}
            {latest && !editing && (
                <Card>
                    <CardContent className="p-3 space-y-2">
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                {isDe ? 'Letzte Messung' : 'Latest measurement'}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                                {new Date(latest.measured_at).toLocaleDateString(isDe ? 'de-DE' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                                {latest.source && latest.source !== 'manual' && latest.source !== 'unknown' ? ` · ${latest.source}` : ''}
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            {KPI_FIELDS.map(key => {
                                const f = FIELDS.find(x => x.key === key);
                                if (!f) return null;
                                const v = latest[key];
                                if (v === null || v === undefined) return null;
                                return (
                                    <div key={key} className="rounded-md bg-muted/40 border border-border/60 p-2" data-testid={`bc-kpi-${key}`}>
                                        <div className="text-[10px] text-muted-foreground truncate">{labelFor(key, language)}</div>
                                        <div className="text-base font-semibold leading-tight">
                                            {fmt(v, f.decimals)}<span className="text-[10px] text-muted-foreground ml-0.5">{f.unit}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Trend chart */}
            {fieldsWithData.length > 0 && !editing && (
                <Card>
                    <CardContent className="p-3 space-y-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">
                                {isDe ? 'Trend' : 'Trend'}
                            </span>
                            {fieldsWithData.map(f => (
                                <button
                                    key={f.key}
                                    onClick={() => setChartField(f.key)}
                                    data-testid={`bc-trend-pick-${f.key}`}
                                    className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${chartField === f.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground'}`}
                                >
                                    {labelFor(f.key, language)}
                                </button>
                            ))}
                        </div>
                        <div className="h-44 -mx-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                                    <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} width={36} />
                                    <Tooltip
                                        contentStyle={{ fontSize: 12, padding: 6, borderRadius: 8 }}
                                        labelStyle={{ fontSize: 11 }}
                                    />
                                    <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Empty state */}
            {!latest && !editing && !loading && (
                <Card>
                    <CardContent className="p-4 text-center space-y-2">
                        <Scale className="w-8 h-8 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                            {isDe ? 'Noch keine Messungen. Lade ein Foto/PDF deines InBody-/Fitdays-/Waagen-Berichts hoch oder gib Werte manuell ein.' : 'No measurements yet. Upload a photo/PDF of your InBody/Fitdays/scale report or enter values manually.'}
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Edit / new entry form */}
            {editing && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                    <Card>
                        <CardContent className="p-3 space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-semibold">{isDe ? 'Werte prüfen & speichern' : 'Review & save'}</div>
                                    {editing.source && editing.source !== 'unknown' && (
                                        <div className="text-[10px] text-muted-foreground">{isDe ? 'Quelle:' : 'Source:'} {editing.source}</div>
                                    )}
                                </div>
                                <button onClick={cancelEdit} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted" data-testid="bc-cancel">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {FIELDS.map(f => (
                                    <div key={f.key} className="space-y-0.5">
                                        <label className="text-[10px] text-muted-foreground block">{labelFor(f.key, language)} {f.unit ? `(${f.unit})` : ''}</label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            inputMode="decimal"
                                            value={editing[f.key] ?? ''}
                                            onChange={(e) => updateField(f.key, e.target.value)}
                                            className="h-8 text-sm"
                                            data-testid={`bc-input-${f.key}`}
                                        />
                                    </div>
                                ))}
                            </div>
                            <Button onClick={saveEntry} className="w-full" data-testid="bc-save-btn">
                                {isDe ? 'Speichern' : 'Save'}
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            )}

            {/* History toggle */}
            {history.length > 0 && !editing && (
                <Card>
                    <CardContent className="p-2">
                        <button
                            className="w-full flex items-center justify-between p-1.5 text-sm font-medium"
                            onClick={() => setShowHistory(s => !s)}
                            data-testid="bc-history-toggle"
                        >
                            <span>{isDe ? 'Verlauf' : 'History'} ({history.length})</span>
                            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {showHistory && (
                            <div className="space-y-1.5 mt-1">
                                {history.map(h => (
                                    <div key={h.id} className="flex items-center gap-2 p-2 rounded-md bg-muted/30 text-xs">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium">
                                                {new Date(h.measured_at).toLocaleDateString(isDe ? 'de-DE' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                {h.source && h.source !== 'manual' && h.source !== 'unknown' ? ` · ${h.source}` : ''}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground truncate">
                                                {h.weight_kg ? `${fmt(h.weight_kg, 1)} kg` : ''}
                                                {h.body_fat_pct ? ` · ${fmt(h.body_fat_pct, 1)} % KFA` : ''}
                                                {h.skeletal_muscle_kg ? ` · ${fmt(h.skeletal_muscle_kg, 1)} kg Skel.Musk.` : ''}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => deleteEntry(h.id)}
                                            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-destructive/10 text-destructive"
                                            data-testid={`bc-delete-${h.id}`}
                                            aria-label="delete"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
